import { storage } from '@abyss/web/tools/storage';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Layout } from '@abyss/web/ui/Layout';
import { Text } from '@abyss/web/ui/Text';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import update from 'lodash/update';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLocation } from '../../../hooks/useLocation';
import { refactorPlaceName } from '../../../utils/locationSearch.util';
import { adobeLinkTrackEvent } from '../../AdobeTagging/adobeLinkTrackEvent';
import { Constants } from '../../Constants';
import { locationSearchImpressionTrackEvent } from '../../Utils/adobeTrackUtils/adobeTrackUtils';
import {
  FindUserLocationContainer,
  StyledLocationError,
  StyledLocationHeader,
  StyledLocationItems,
  StyledLocationSearch,
} from './LocationSearchDesktop.styled';

type Props = {
  findUserLocation: Function;
  location: string;
  setLocation: (a: string) => void;
};

export const LocationSearchDesktop = ({
  findUserLocation,
  location = storage.session.get(Constants.STORAGE_KEYS.LOCAL.LOCATION),
  setLocation,
}: Props) => {
  const { t } = useTranslation();
  const [isOnFocus, setIsOnFocus] = useState(false);
  const locationSearchInputDataTestId: string = 'location-search-input';
  const locationForAnalytics: string = 'change search location';

  const [inputVal, setInputVal] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [gqlSuggestions, setgqlSuggestions] = useState<object[]>([
    {
      section: (
        <div id="instructional-text">
          {t('LOCATION_SEARCH.HEADER_DECEMBER_RELEASE')}
        </div>
      ),
      items: [],
    },
  ]);

  const [, getLocation] = useLocation({
    onCompleted: (result: {
      data: {
        location: {
          features: [{ place_name: string; id: string }];
        };
      };
    }) => {
      const resultSuggestions = cloneDeep(result?.data?.location?.features);
      resultSuggestions?.map((loc) =>
        update(loc, 'place_name', () => refactorPlaceName(loc.place_name))
      );
      const findSuggestion =
        resultSuggestions?.length > 0 &&
        resultSuggestions.concat({
          place_name: 'find',
          id: 'find',
        });

       
      !isEmpty(resultSuggestions)
        ? setgqlSuggestions([
            {
              section: t('LOCATION_SEARCH.HEADER_DECEMBER_RELEASE'),
              items: findSuggestion,
            },
          ])
        : setgqlSuggestions([
            {
              section: t('LOCATION_SEARCH.HEADER_DECEMBER_RELEASE'),
              items: [],
            },
            {
              section: t('LOCATION_SEARCH.ERR_DEC_RELEASE'),
              items: [],
            },
          ]);
    },
  });

  const handleLocationChange = async (loc: string) => {
    const { data } = await getLocation({
      variables: {
        address: encodeURIComponent(loc),
        countySearchEnabled: true,
      },
    });
    if (data) {
      locationSearchImpressionTrackEvent(loc, data.location?.features?.length);
    }
  };

  const debounceApiFilteringSearch = useCallback(
    debounce(async (inputValue: string) => {
      if (inputValue?.trim().length) {
        await handleLocationChange(inputValue);
      }
      setIsLoading(false);
    }, 300),
    [inputVal]
  );

  const adobeLinkTrackEventFindLocation = () =>
    adobeLinkTrackEvent({
      name: Constants.LOCATION_SEARCH.FIND_MY_LOCATION,
      location: `body:${locationForAnalytics}`,
      type: 'internal',
    });

  const adobeLinkTrackEventSuggestedLocation = () =>
    adobeLinkTrackEvent({
      name: 'suggested location',
      location: `body:${locationForAnalytics}`,
      type: 'internal',
    });

  const handleClear = () => {
    setgqlSuggestions([
      {
        section: t('LOCATION_SEARCH.HEADER_DECEMBER_RELEASE'),
        items: [],
      },
    ]);
    setInputVal('');
  };

  const handleLocationInputChange = (inputValue) => {
    if (inputValue?.trim().length) {
      setIsLoading(true);
      setInputVal(inputValue);
    } else {
      handleClear();
    }
  };

  useEffect(() => {
    // Remove <span> element with role="status" for a11y fix
    const statusElement = document.querySelector('span[role="status"]');
    if (statusElement && statusElement?.isConnected) {
      // Safe removal: only removes if element is still in DOM
      // Prevents "node is not a child of this node" errors during rapid rerenders
      statusElement?.remove();
    }

    // Find the element based on its test id
    const element = document.querySelector(
      '[data-testid="location-search-input-abyss-search-input-input"]'
    );

    // Check if the element exists
    if (element) {
      // Add the data-describedby attribute with a value
      element.setAttribute('aria-describedby', 'instructional-text');
    }
  }, [isOnFocus]);

  const handleSearch = (newLocation) => {
    if (newLocation.place_name === 'find') {
      findUserLocation();
      adobeLinkTrackEventFindLocation();
    } else {
      setLocation(newLocation.place_name);
      adobeLinkTrackEventSuggestedLocation();
    }
  };

  return (
    <StyledLocationSearch
      apiFiltering={debounceApiFilteringSearch}
      customRender={(suggestion) =>
         
        suggestion.section ? (
          suggestion.section !== t('LOCATION_SEARCH.ERR_DEC_RELEASE') &&
          suggestion.section !== t('LOCATION_SEARCH.ERROR') ? (
            <StyledLocationHeader>
              {suggestion.section}
              {!inputVal.includes('Search') && suggestion.items?.length > 0
                ? t(`LOCATION_SEARCH.DID_YOU_MEAN`)
                : ''}
            </StyledLocationHeader>
          ) : (
            <StyledLocationError alignItems="top">
              <IconMaterial color="$error1" icon="error" size="$sm" />
              <Text color="$error1" size="14.22px">
                {suggestion.section}
              </Text>
            </StyledLocationError>
          )
        ) : suggestion.place_name !== 'find' ? (
          <StyledLocationItems grow space={0}>
            <Text color="$accent1" fontWeight="$bold" size="$sm">
              {suggestion.place_name}
            </Text>
          </StyledLocationItems>
        ) : (
          <StyledLocationItems>
            <FindUserLocationContainer
              data-auto-testid="find-user-location-clickable-element"
              data-testid="find-user-location-clickable-element"
              onClick={() => {
                handleClear();
                findUserLocation();
              }}
            >
              <Layout.Group space={4}>
                <IconMaterial
                  css={{ marginRight: '10px' }}
                  icon="my_location"
                />
                <Text color="$interactive1" fontWeight="$bold" size="sm">
                  {t('Find my location')}
                </Text>
              </Layout.Group>
            </FindUserLocationContainer>
          </StyledLocationItems>
        )
      }
      data-auto-testid={locationSearchInputDataTestId}
      data-testid={locationSearchInputDataTestId}
      hideLabel
      inputLeftElement={
        <IconMaterial color="$gray8" icon="location_on" size={24} />
      }
      isLoading={isLoading}
      keys={['value', 'location']}
      label={t('Location')}
      onClear={handleClear}
      onFocus={() => setIsOnFocus(true)}
      onInputChange={handleLocationInputChange}
      onSearch={handleSearch}
      openOnFocus
      options={gqlSuggestions}
      placeholder={
        location || t('LOCATION_SEARCH.PLACEHOLDER_DECEMBER_RELEASE')
      }
      rounded
      value={location}
    />
  );
};
